import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, FileText, Presentation, ShieldCheck, Mail, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

export default function Espace() {
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const fr = lang === "fr";

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const docs = [
    { icon: FileText, t: fr ? "Brief du projet" : "Project brief", d: fr ? "Synthèse technique et institutionnelle (PDF)." : "Technical & institutional summary (PDF)." },
    { icon: Presentation, t: fr ? "Dossier d'investissement" : "Investment deck", d: fr ? "Modèle financier détaillé et structure de financement." : "Detailed financial model and financing structure." },
    { icon: ShieldCheck, t: fr ? "Documents de garantie" : "Guarantee documents", d: fr ? "PPA, garantie souveraine et accords partenaires." : "PPA, sovereign guarantee and partner agreements." },
  ];

  return (
    <section className="min-h-[80vh] py-16 sm:py-20 bg-slate-50 noise-bg">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="bg-sky-900 rounded-2xl p-8 sm:p-10 text-white flex flex-col sm:flex-row sm:items-center gap-6 justify-between"
          data-testid="espace-header"
        >
          <div className="flex items-center gap-5">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-white/20 object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-2xl font-display">{user?.name?.[0] || "?"}</div>
            )}
            <div>
              <div className="mono text-xs tracking-widest uppercase text-sky-300">{fr ? "Espace Investisseur" : "Investor Area"}</div>
              <h1 className="font-display text-2xl sm:text-3xl font-light mt-1" data-testid="espace-user-name">
                {fr ? "Bienvenue" : "Welcome"}, {user?.name?.split(" ")[0]}
              </h1>
              <div className="text-sky-200 text-sm mt-1">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="espace-logout"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 text-sm font-medium hover:bg-white/10 active:scale-95 transition-[background-color,transform] duration-200"
          >
            <LogOut className="w-4 h-4" />{fr ? "Déconnexion" : "Sign out"}
          </button>
        </motion.div>

        <div className="mt-8">
          <div className="mono text-xs tracking-widest uppercase text-secondary mb-4">{fr ? "Documents confidentiels" : "Confidential documents"}</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {docs.map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col"
                data-testid={`espace-doc-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center mb-4">
                  <d.icon className="w-5 h-5 text-sky-700" />
                </div>
                <div className="font-display text-lg text-slate-900">{d.t}</div>
                <p className="text-sm text-slate-600 mt-1 flex-1">{d.d}</p>
                <button
                  onClick={() => navigate("/contact")}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-sky-700 hover:text-sky-900 transition-colors"
                >
                  <Download className="w-4 h-4" />{fr ? "Demander l'accès" : "Request access"}
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-display text-xl text-slate-900">{fr ? "Une question sur le projet ?" : "A question about the project?"}</div>
            <p className="text-slate-600 text-sm mt-1">{fr ? "Notre équipe investissement est à votre disposition." : "Our investment team is at your service."}</p>
          </div>
          <button
            onClick={() => navigate("/contact")}
            data-testid="espace-contact"
            className="inline-flex items-center gap-2 rounded-full bg-secondary text-white px-6 py-3 font-medium hover:bg-green-700 active:scale-95 transition-[background-color,transform] duration-200"
          >
            <Mail className="w-4 h-4" />{fr ? "Contacter l'équipe" : "Contact the team"}
          </button>
        </div>
      </div>
    </section>
  );
}
